import type { ConfigProviderProps } from 'antd/es/config-provider';
import type { RootScope } from '../types';

import React from 'react';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import * as Sentry from '@sentry/react';
import { ConfigProvider } from 'antd';

import { dayjs } from '../services/dayjs';
import { getRootScope } from './ngProvider';
import { CompanyContext, CompanyContextProps } from './useCompany';

import './theme.scss';

const colorPrimary = '#f78b1c';
const colorSecondary = '#272747';

export const ContextProvider: React.FC<{
  children: React.ReactElement;
  $rootScope?: RootScope;
}> = ({ children, $rootScope = getRootScope() }) => {
  const [company, setCompany] = React.useState<CompanyContextProps>();
  const [antdMessages, setAntdMessages] =
    React.useState<ConfigProviderProps['locale']>();

  React.useEffect(() => {
    if (!$rootScope) {
      throw new Error(`Expected root scope to be set`);
    }

    (async () => {
      const locale = $rootScope.company.ui_locale;
      const { messages, antdMessages: newAntdMessages } =
        await getLocaleMessages(locale);

      i18n.load(locale, messages);
      i18n.activate(locale);
      dayjs.updateLocale(locale, {
        weekStart: 1, // 0 for Sunday, 1 for Monday
      });
      dayjs.locale(locale);

      setCompany({
        country: $rootScope.getDefaultCountry(),
      });
      setAntdMessages(newAntdMessages);
    })();
  }, [$rootScope]);

  if (!company) {
    return null;
  }

  return (
    <Sentry.ErrorBoundary
      fallback={
        <p>An error has occurred. Please try again or contact support</p>
      }
    >
      <CompanyContext.Provider value={company}>
        <ConfigProvider
          theme={{
            // see: https://ant.design/theme-editor
            cssVar: true,
            hashed: false,
            token: {
              colorPrimary,
              colorInfo: colorPrimary,
              borderRadius: 2,
              fontFamily: `'Open Sans', sans-serif`,
            },
            components: {
              Tooltip: {
                // tooltip bg color
                colorBgSpotlight: colorSecondary,
              },
            },
          }}
          locale={antdMessages}
        >
          <I18nProvider i18n={i18n}>{children}</I18nProvider>
        </ConfigProvider>
      </CompanyContext.Provider>
    </Sentry.ErrorBoundary>
  );
};

async function loadAntdLocale(locale: string) {
  switch (locale) {
    case 'uk':
      return import('antd/locale/uk_UA');
    case 'ru':
      return import('antd/locale/ru_RU');
    case 'vi':
      return import('antd/locale/vi_VN');
    default:
      return import('antd/locale/en_US');
  }
}

async function loadDayjsLocale(locale: string) {
  switch (locale) {
    case 'uk':
      return import('dayjs/locale/uk');
    case 'ru':
      return import('dayjs/locale/ru');
    case 'vi':
      return import('dayjs/locale/vi');
    default:
      return import('dayjs/locale/en');
  }
}

async function getLocaleMessages(locale: string): Promise<{
  messages: { [key: string]: any };
  antdMessages: ConfigProviderProps['locale'];
}> {
  const [{ messages }, antdMessagesMod] = await Promise.all([
    import(`@lingui/loader!../../../i18n/${locale}.po`),
    loadAntdLocale(locale),
    loadDayjsLocale(locale),
  ]);

  return {
    messages,
    antdMessages: antdMessagesMod.default,
  };
}
